import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ActiveModal, TermsModal } from "../components/Modal"
import { CalendarIcon, IconEvaluations, IconQr, IconRanking, IconUser, IconSettings, IconAssistant, IconChat, WebinarIcon } from "../components/Icons";
import { useDeviceType } from "../hooks/useMediaQuery";
import { useResto } from "../hooks/useResto";
import OwnerRankingPage from "../features/resto/desktop/pages/OwnerRankingPage";
import { ChatTab } from "../features/resto/desktop/pages/ChatPage";
import { ComunicacionPage } from "../features/resto/desktop/pages/ComunicacionPage";
import { useTransaction } from "../hooks/useTransaction";
import { usePromociones } from "../context/PromocionesContext";
import { ChatMobileTab } from "../features/resto/mobile/pages/ChatTab";
import { ComunicacionMobileTab } from "../features/resto/mobile/pages/ComunicacionMobileTab";
import { AIAssistantTab } from "../components/AIAssistantTab";
import { AIAssistantMobileTab } from "../components/AIAssistantMobileTab";
import { EvaluationTab as EvaluationsTab } from "../features/resto/mobile/pages/EvaluationsTab";
import { useHost } from "../hooks/userInfoService";
import { Qrpage } from "../features/host/pages/QrPage";
import { useChat } from "../hooks/useChat";
import { useUser } from "../hooks/useUser";
import { RankingHostCEOTab } from "../features/resto/mobile/pages/RankingHostCeo";
import { EvaluationCeoTab } from "../features/resto/mobile/pages/EvaluationCeoPage";
import { PendingApprovalsTab } from "../features/resto/mobile/pages/PendingApprovalsTab";
import BottomBar from "../components/BottomBar";
import Header from "../components/Header";
import DesktopPageHeader from "../components/DesktopPageHeader";
import Dashboard from "./DashboardPage";
import RankingPage from "../features/host/pages/RankingPage";
import ShiftPage from "../features/host/pages/ShiftPage";
import ChatNotifyIcon from "../components/ChatNotifyIcon";
import QuizHome from "../features/quiz/pages/QuizHome";
import QuizAdminHome from "../features/quiz/pages/QuizAdminHome";
import OwnerTriviasSection from "../features/quiz/pages/OwnerTriviasSection";
import TriviaMarketplace from "../features/quiz/pages/TriviaMarketplace";
import OwnerSettingsPage from "../features/owner/pages/OwnerSettingsPage";
import AtmCodesPage from "../features/resto/desktop/pages/AtmCodesPage";
import AcademyHome from "../features/academy/pages/AcademyHome";
import WebinarsPage from "./Webinars";
import { useOwnerRestaurantSelection } from "../hooks/useOwnerRestaurantSelection";
import { ReactComponent as TeacherIcon } from "../assets/teacher.svg";
import { ReactComponent as VideoCircleIcon } from "../assets/video-circle.svg";
import { updateTenantRestaurant } from "../services/tenantOwnerService";
import { ReactComponent as BagIcon } from "../assets/bag.svg";
import { FEATURE_FLAGS } from "../config/featureFlags";

const normalizeCountry = (country) =>
    String(country || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const resolveCountryLabel = (country) => {
    const normalizedCountry = normalizeCountry(country);

    switch (normalizedCountry) {
        case "colombia":
        case "co":
            return "Colombia";
        case "peru":
        case "pe":
            return "Perú";
        case "bolivia":
        case "bo":
            return "Bolivia";
        default:
            return String(country || "").trim();
    }
};

const OwnerMobileView = ({ children }) => <>{children}</>;

const TAB_LABELS = {
    "host-ranking": "Ranking",
    "host-profile": "Configuración",
    "host-qr": "Mi QR",
    "host-quiz": "Trivias",
    "host-asistente": "ALMA",
    "host-chat": "Chat",
    "almacen": "Tu tienda",
    "resto-evaluation": "Evaluaciones",
    "resto-home": "Ranking",
    "resto-qr": "Mi QR",
    "resto-quiz": "Trivias",
    "resto-marketplace": "Marketplace",
    "resto-asistente": "ALMA",
    "resto-chat": "Chat",
    "resto-comunicacion": "Novedades",
    "ajustes": "Ajustes",
    "resto-approvals": "Aprobaciones",
    "atm-codes": "Códigos",
    "host-academy": "AcademIA",
    "resto-academy": "AcademIA",
    "atm-academy": "AcademIA",
    "webinars": "Comunidad TiendIA",
    "ceo-academy": "AcademIA",
    "ceo-home": "Restaurantes",
    "ceo-host": "Hosts",
    "ceo-evaluation": "Evaluaciones",
    "ceo-quiz": "Trivias",
};

function splitMobileTabs(tabs, options = {}) {
    if (!Array.isArray(tabs) || tabs.length === 0) {
        return { bottomBarTabs: [], menuTabs: [] };
    }
    const ids = tabs.map((t) => t.id);
    const { bottomOrder } = options;

    // Si se provee un orden explícito, se respeta (hasta 6 tabs) y el resto va al menú overflow.
    if (Array.isArray(bottomOrder) && bottomOrder.length > 0) {
        const orderedIds = bottomOrder.filter((id) => ids.includes(id)).slice(0, 6);
        const bottomBarTabs = orderedIds
            .map((id) => tabs.find((t) => t.id === id))
            .filter(Boolean);
        const menuTabs = tabs.filter((t) => !orderedIds.includes(t.id));
        return { bottomBarTabs, menuTabs };
    }

    const triviaId = ids.find((id) => id.includes("quiz"));
    const assistantId = ids.find((id) => id.includes("asistente"));
    const evaluationId = ids.find((id) => id.includes("evaluation"));

    let mainId = null;
    if (ids.includes("almacen")) mainId = "almacen";
    else if (ids.includes("host-qr")) mainId = "host-qr";
    else if (ids.includes("atm-codes")) mainId = "atm-codes";
    else if (ids.includes("ceo-home")) mainId = "ceo-home";
    else if (ids.includes("resto-home")) mainId = "resto-home";
    else if (ids.includes("host-profile")) mainId = "host-profile";

    const orderedIds = [];
    if (triviaId) orderedIds.push(triviaId);
    if (mainId) orderedIds.push(mainId);
    if (ids.includes("webinars")) orderedIds.push("webinars");
    if (assistantId) orderedIds.push(assistantId);
    if (evaluationId) orderedIds.push(evaluationId);

    tabs.forEach((tab) => {
        if (!orderedIds.includes(tab.id)) {
            orderedIds.push(tab.id);
        }
    });

    const bottomIds = orderedIds.slice(0, 6);
    const bottomBarTabs = bottomIds
        .map((id) => tabs.find((t) => t.id === id))
        .filter(Boolean);
    const menuTabs = tabs.filter((t) => !bottomIds.includes(t.id));

    return { bottomBarTabs, menuTabs };
}

function enrichWithLabels(tabs) {
    return tabs.map((tab) => ({
        ...tab,
        label: tab.label || TAB_LABELS[tab.id] || tab.id,
    }));
}

const MainPage = ({ props }) => {
    const history = useHistory();
    const location = useLocation();
    const { isDesktop } = useDeviceType()
    const { resto, getResto } = useResto();
    const { infoHost, hostActive, openModalTerms, getDataHost, setActiveHost, handleAccept, closeModalTerms } = useHost();
    const { points, pointsLoader, getPoints } = useTransaction();
    const { avatar, isHost, isResto, isAtm, isOwner, type, isCEO, imageUpload } = useUser();
    const { ownerRestaurants, selectedOwnerRestaurant, selectedOwnerRestaurantId, reloadOwnerRestaurants } = useOwnerRestaurantSelection();
    const { messagesNoReadLength } = useChat();
    const { unreadCount } = usePromociones();
    const isTriviaMarketplaceRoute = location.pathname === "/almacen/trivias/marketplace";
    const requestedInitialTab = new URLSearchParams(location.search).get("tab");
    const normalizedInitialTab =
        requestedInitialTab === "resto-hosts" || requestedInitialTab === "hosts"
            ? (isDesktop ? "asistente" : "resto-asistente")
            : requestedInitialTab;
    const [activeTab, setActiveTab] = useState(
        normalizedInitialTab ||
        (isTriviaMarketplaceRoute && isOwner
            ? (isDesktop ? "trivias-marketplace" : "resto-quiz")
            : isCEO ? "ceo-quiz" : isOwner ? (isDesktop ? "asistente" : "resto-asistente") : isAtm ? "atm-codes" : isResto ? "resto-quiz" : "host-profile")
    );
    const [title, setTitle] = useState("")
    const [showProfile, setShowProfile] = useState(true)
    const [showWelcome, setShowWelcome] = useState(isHost)
    const [showRestoAddress, setShowRestoAddress] = useState(isHost)
    const [showResto, setShowResto] = useState(isHost)
    const [showName, setShowName] = useState(isHost)

    const update = useCallback(() => {
        getResto();
        if (isHost) getDataHost();
    }, [getDataHost, getResto, isHost]);

    useEffect(() => {
        update();
        getPoints();
    }, [getPoints, update])

    const canSeeHostRanking = true;
    const canSeeRestoRanking = true;
    const canSeeEvaluations = true;
    const canSeeHostChat = true;
    const uiType = isOwner ? "resto" : type;

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const requestedTab = query.get("tab");
        if (requestedTab === "resto-hosts" || requestedTab === "hosts") {
            const fallbackTab = isDesktop ? "asistente" : "resto-asistente";
            query.set("tab", fallbackTab);
            history.replace({ pathname: location.pathname, search: query.toString() });
        }
    }, [history, location.pathname, location.search]);

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const tabFromUrl = query.get("tab");
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [location.search]);

    useEffect(() => {
        if (!isOwner || !isTriviaMarketplaceRoute) return;
        const nextTab = isDesktop ? "trivias-marketplace" : "resto-marketplace";
        if (activeTab !== nextTab) {
            setActiveTab(nextTab);
        }
        const query = new URLSearchParams(location.search);
        const targetTab = isDesktop ? "trivias-marketplace" : "resto-marketplace";
        if (location.pathname !== "/" || query.get("tab") !== targetTab || query.get("quizView") !== "marketplace") {
            query.set("tab", targetTab);
            query.set("quizView", "marketplace");
            history.replace({ pathname: "/", search: query.toString() });
        }
    }, [activeTab, history, isDesktop, isOwner, isTriviaMarketplaceRoute, location.pathname, location.search]);

    useEffect(() => {
        if (FEATURE_FLAGS.COMUNICACION_ENABLED) return;

        const query = new URLSearchParams(location.search);
        const requestedTab = query.get("tab");
        const blockedTabs = ["resto-chat"];

        if (!blockedTabs.includes(requestedTab)) return;

        const fallbackTab = isDesktop
            ? isAtm
                ? "codigos"
                : "asistente"
            : isOwner
                ? "resto-asistente"
                : isAtm
                    ? "atm-codes"
                    : isResto
                        ? "resto-home"
                        : "host-qr";

        query.set("tab", fallbackTab);
        query.delete("chatMention");
        history.replace({ pathname: location.pathname, search: query.toString() });
    }, [history, isAtm, isDesktop, isOwner, isResto, location.pathname, location.search]);

    const wrapOwnerMobile = (component) => <OwnerMobileView>{component}</OwnerMobileView>;
    const selectedOwnerRestoName = selectedOwnerRestaurant?.name || "";
    const selectedOwnerRestoAddress = selectedOwnerRestaurant?.address || "";
    const selectedOwnerRestoId = selectedOwnerRestaurant?._id || selectedOwnerRestaurant?.id || "";
    const selectedOwnerRestoAvatar =
        selectedOwnerRestaurant?.brand ||
        selectedOwnerRestaurant?.avatar ||
        selectedOwnerRestaurant?.cover ||
        selectedOwnerRestaurant?.logo ||
        selectedOwnerRestaurant?.image ||
        "";
    const evaluationRestoName = isOwner ? selectedOwnerRestoName : resto?.name || "";
    const evaluationRestoAddress = isOwner ? selectedOwnerRestoAddress : resto?.address || "";
    const evaluationRestoId = isOwner ? selectedOwnerRestoId : resto?._id || resto?.id || "";
    const currentRestoAvatar =
        resto?.brand ||
        resto?.avatar ||
        resto?.cover ||
        resto?.logo ||
        resto?.image ||
        "";
    const evaluationRestoAvatar = isOwner ? selectedOwnerRestoAvatar : currentRestoAvatar;
    const evaluationsContent = (
        <DesktopContainer>
            <DesktopPageHeader img={evaluationRestoAvatar} alt={evaluationRestoName} title={evaluationRestoName} />
            <EvaluationsTab restoName={evaluationRestoName} forceRestaurantOnly={isOwner} />
        </DesktopContainer>
    );
    const ownerTriviasContent = (
        <DesktopContainer>
            <DesktopPageHeader img={selectedOwnerRestoAvatar} alt={selectedOwnerRestoName} title={selectedOwnerRestoName} />
            <OwnerTriviasSection />
        </DesktopContainer>
    );
    const ownerMarketplaceContent = (
        <DesktopContainer>
            <DesktopPageHeader img={selectedOwnerRestoAvatar} alt={selectedOwnerRestoName} title={selectedOwnerRestoName} />
            <TriviaMarketplace />
        </DesktopContainer>
    );
    const qrContent = (
        <DesktopContainer>
            <DesktopPageHeader img={evaluationRestoAvatar} alt={evaluationRestoName} title={evaluationRestoName} />
            <Qrpage showInfo />
        </DesktopContainer>
    );
    const rankingContent = (
        <DesktopContainer>
            <OwnerRankingPage resto={isOwner ? selectedOwnerRestaurant : resto} />
        </DesktopContainer>
    );
    const academyContent = (
        <DesktopContainer>
            <DesktopPageHeader img={avatar} alt="AcademIA" title={isOwner ? selectedOwnerRestoName : resto?.name || ""} />
            <AcademyHome />
        </DesktopContainer>
    );
    const desktopTabs = [
        ...(FEATURE_FLAGS.AI_ASSISTANT_ENABLED ? [{
            id: "asistente",
            label: "ALMA",
            icon: <IconAssistant />,
            content: <AIAssistantTab resto={resto} />
        }] : []),
        {
            id: "qr",
            label: "Mi QR",
            icon: <IconQr />,
            content: qrContent
        },
        {
            id: "ranking",
            label: "Ranking",
            icon: <IconRanking />,
            content: rankingContent
        },
        {
            id: "evaluaciones",
            label: "Evaluaciones",
            icon: <IconEvaluations />,
            content: evaluationsContent
        },
        {
            id: "trivias",
            label: "Trivias",
            icon: <TeacherIcon />,
            content: isOwner ? ownerTriviasContent : <QuizAdminHome />
        },
        ...(isOwner
            ? [{
                id: "trivias-marketplace",
                label: "Marketplace",
                icon: <BagIcon />,
                content: ownerMarketplaceContent
            }]
            : []),
        ...(FEATURE_FLAGS.COMUNICACION_ENABLED ? [{
            id: "comunicacion",
            label: "Novedades",
            icon: <IconChat width="20" height="20" />,
            badge: unreadCount > 0,
            content: <ComunicacionPage resto={resto} />
        }] : []),
        ...(FEATURE_FLAGS.WEBINARS_ENABLED ? [{
            id: "webinars",
            label: "Comunidad TiendIA",
            icon: <WebinarIcon />,
            content: <WebinarsPage />
        }] : []),
        {
            id: "academy",
            label: "AcademIA",
            icon: <VideoCircleIcon />,
            content: academyContent
        },
        ...(isOwner
            ? [{ id: "ajustes", label: "Ajustes", icon: <IconSettings />, content: <OwnerSettingsPage /> }]
            : []),
    ]

    const desktopTabsAtm = [
        {
            id: "codigos",
            label: "Códigos",
            icon: <IconQr />,
            content: <AtmCodesPage />
        }
    ];
    const configTab = (title = "", profile = false, short = false) => {
        setTitle(title)
        setShowProfile(profile);
        setShowWelcome(!short);
        setShowRestoAddress(!short);
        setShowResto(!short);
        setShowName(!short);
    }

    const tabsMobileUser = [
        ...(canSeeHostRanking ? [{ id: "host-ranking", icon: <IconRanking />, component: <RankingPage /> }] : []),
        { id: "host-profile", icon: <CalendarIcon />, component: <ShiftPage /> },
        { id: "host-qr", icon: <IconQr />, component: <Qrpage /> },
        { id: "host-quiz", icon: <TeacherIcon />, component: <QuizHome /> },
        ...(FEATURE_FLAGS.WEBINARS_ENABLED ? [{ id: "webinars", icon: <WebinarIcon />, label: "", component: <WebinarsPage /> }] : []),
        { id: "host-academy", icon: <VideoCircleIcon />, component: <AcademyHome /> },
        ...(FEATURE_FLAGS.AI_ASSISTANT_ENABLED ? [{ id: "host-asistente", icon: <IconAssistant />, component: <AIAssistantMobileTab /> }] : []),
        ...(canSeeHostChat ? [{ id: "host-chat", icon: <ChatNotifyIcon count={messagesNoReadLength || 0} type="host" />, component: <ChatMobileTab /> }] : []),
    ];

    const tabsMobileResto = isOwner
        ? [
            { id: "resto-evaluation", icon: <IconEvaluations />, component: wrapOwnerMobile(<EvaluationsTab restoName={resto?.name} forceRestaurantOnly />) },
            { id: "resto-home", icon: <IconRanking />, component: wrapOwnerMobile(<OwnerRankingPage resto={resto} />) },
            { id: "resto-qr", icon: <IconQr />, component: wrapOwnerMobile(<Qrpage />) },
            { id: "resto-quiz", icon: <TeacherIcon />, component: wrapOwnerMobile(<OwnerTriviasSection />) },
            { id: "resto-marketplace", icon: <BagIcon />, component: wrapOwnerMobile(<TriviaMarketplace />) },
            { id: "resto-academy", icon: <VideoCircleIcon />, component: wrapOwnerMobile(<AcademyHome />) },
            ...(FEATURE_FLAGS.AI_ASSISTANT_ENABLED ? [{ id: "resto-asistente", icon: <IconAssistant />, component: wrapOwnerMobile(<AIAssistantMobileTab />) }] : []),
            ...(FEATURE_FLAGS.COMUNICACION_ENABLED ? [{ id: "resto-comunicacion", icon: <IconChat width="20" height="20" />, badge: unreadCount > 0, component: wrapOwnerMobile(<ComunicacionMobileTab />) }] : []),
            ...(FEATURE_FLAGS.WEBINARS_ENABLED ? [{ id: "webinars", icon: <WebinarIcon />, label: "", component: wrapOwnerMobile(<WebinarsPage />) }] : []),
            { id: "ajustes", icon: <IconSettings />, component: <OwnerSettingsPage /> },
        ]
        : [
            { id: "resto-home", icon: <IconRanking />, component: <OwnerRankingPage resto={resto} /> },
            { id: "resto-evaluation", icon: <IconEvaluations />, component: <EvaluationsTab restoName={resto?.name} /> },
            { id: "resto-qr", icon: <IconQr />, component: <Qrpage /> },
            { id: "resto-quiz", icon: <TeacherIcon />, component: <QuizAdminHome /> },
            { id: "resto-academy", icon: <VideoCircleIcon />, component: <AcademyHome /> },
            {
                id: "resto-approvals",
                icon: <IconUser />,
                component: <PendingApprovalsTab />
            },
            ...(FEATURE_FLAGS.AI_ASSISTANT_ENABLED ? [{ id: "resto-asistente", icon: <IconAssistant />, component: <AIAssistantMobileTab /> }] : []),
            ...(FEATURE_FLAGS.COMUNICACION_ENABLED ? [{ id: "resto-comunicacion", icon: <IconChat width="20" height="20" />, badge: unreadCount > 0, component: <ComunicacionMobileTab /> }] : []),
            ...(FEATURE_FLAGS.WEBINARS_ENABLED ? [{ id: "webinars", icon: <WebinarIcon />, label: "", component: <WebinarsPage /> }] : []),
        ];

    const tabsMobileAtm = [
        { id: "atm-codes", icon: <IconQr />, component: <AtmCodesPage /> },
        { id: "atm-academy", icon: <VideoCircleIcon />, component: <AcademyHome /> },
    ];

    const tabsMobileRestoCeo = [
        ...(canSeeRestoRanking ? [{ id: "ceo-home", icon: <IconRanking />, component: <OwnerRankingPage resto={resto} /> }] : []),
        /* { id: "ceo-host", icon: <IconUser />, component: <RankingHostCEOTab /> }, */
        ...(canSeeEvaluations ? [{ id: "ceo-evaluation", icon: <IconEvaluations />, component: <EvaluationCeoTab /> }] : []),
        { id: "ceo-quiz", icon: <TeacherIcon />, component: <QuizAdminHome /> },
        { id: "ceo-academy", icon: <VideoCircleIcon />, component: <AcademyHome /> },
    ];

    const tabsRestoActions = {
        "resto-home": () => configTab("", true, true),
        "resto-qr": () => configTab("", true, false),
        "resto-academy": () => configTab("AcademIA", false, true),
        "resto-approvals": () => configTab("Aprobaciones pendientes", false, true),
        "resto-asistente": () => configTab("", false, true),
        "resto-chat": () => configTab("Motiva a tu equipo", false, true),
        "resto-comunicacion": () => configTab("", false, true), // Título se muestra dentro de la propia tab
        "resto-evaluation": () => configTab("", false, true),
        "resto-quiz": () => configTab("Trivias", false, true),
        "resto-marketplace": () => configTab("", false, true),
        "webinars": () => configTab("", false, true),
        "ajustes": () => configTab("Ajustes", false, true),
        "host-qr": () => configTab("", true, false),
        "host-ranking": () => configTab("Ranking", false, true),
        "host-profile": () => configTab("Configuración semanal", false, true),
        "host-quiz": () => configTab("Trivias", false, true),
        "host-academy": () => configTab("AcademIA", false, true),
        "host-asistente": () => configTab("", false, true),
        "host-chat": () => configTab("Inbox", false, true),
        "ceo-home": () => configTab("Restaurantes", false, true),
        "ceo-host": () => configTab("Hosts", false, true),
        "ceo-evaluation": () => configTab("", false, true),
        "ceo-quiz": () => configTab("Trivias", false, true),
        "ceo-academy": () => configTab("AcademIA", false, true),
        "atm-codes": () => configTab("Códigos", false, true),
        "atm-academy": () => configTab("AcademIA", false, true),
        "academy": () => configTab("AcademIA", false, true),
    }

    const handleSelectTab = (tabId) => {
        setActiveTab(tabId);
        if (!isDesktop) {
            tabsRestoActions[tabId]?.();
        }
        const query = new URLSearchParams(location.search);
        if (query.get("tab") !== tabId) {
            query.set("tab", tabId);
            history.replace({ pathname: location.pathname, search: query.toString() });
        }
    }

    const tabsMobile = isCEO ? tabsMobileRestoCeo : isAtm ? tabsMobileAtm : isResto || isOwner ? tabsMobileResto : tabsMobileUser;
    const ownerBottomOrder = ["resto-qr", "resto-quiz", "resto-marketplace", "webinars", "resto-asistente", "resto-comunicacion", "resto-academy"];
    const { bottomBarTabs, menuTabs } = splitMobileTabs(tabsMobile, { bottomOrder: isOwner ? ownerBottomOrder : undefined });
    const menuTabsWithLabels = enrichWithLabels(menuTabs);
    const mobileTabIds = tabsMobile.map((tab) => tab.id).join(",");

    useEffect(() => {
        if (isDesktop || !tabsMobile.length) return;
        if (tabsMobile.some((tab) => tab.id === activeTab)) {
            tabsRestoActions[activeTab]?.();
            return;
        }

        const preferredTab = isCEO
            ? "ceo-quiz"
            : isAtm
                ? "atm-codes"
                : isOwner
                    ? "resto-asistente"
                    : isResto
                        ? "resto-quiz"
                        : "host-profile";
        const fallbackTab =
            tabsMobile.find((tab) => tab.id === preferredTab)?.id ||
            tabsMobile[0]?.id;

        if (!fallbackTab) return;

        setActiveTab(fallbackTab);
        tabsRestoActions[fallbackTab]?.();

        const query = new URLSearchParams(location.search);
        if (query.get("tab")) {
            query.set("tab", fallbackTab);
            history.replace({ pathname: location.pathname, search: query.toString() });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, history, isAtm, isCEO, isDesktop, isOwner, isResto, location.pathname, location.search, mobileTabIds]);

    if (isDesktop && !isCEO && isAtm) {
        return <Dashboard tabs={desktopTabsAtm} />
    }

    if (isDesktop && !isCEO && type === "resto") {
        return <Dashboard tabs={desktopTabs} />
    }

    if (isDesktop && !isCEO && isOwner) {
        return <Dashboard tabs={desktopTabs} />
    }

    const rawPromotionCountry = resto?.country || infoHost?.country || "";
    const logoCountry = resolveCountryLabel(rawPromotionCountry);
    const isShowingRestoImage = isOwner || isResto || isAtm || isCEO;
    const headerImage = isOwner
        ? selectedOwnerRestoAvatar || avatar
        : isResto || isAtm || isCEO
            ? currentRestoAvatar || avatar
            : avatar;

    const handleHeaderImageUpload = async (file) => {
        if (isOwner && selectedOwnerRestaurant) {
            const restoId = selectedOwnerRestaurant._id || selectedOwnerRestaurant.id;
            if (!restoId) throw new Error("No se pudo identificar la tienda.");
            const payload = new FormData();
            payload.append("file", file);
            await updateTenantRestaurant(restoId, payload, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await reloadOwnerRestaurants(true);
            return;
        }

        if ((isResto || isAtm || isCEO) && resto?._id) {
            const payload = new FormData();
            payload.append("file", file);
            await updateTenantRestaurant(resto._id, payload, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await getResto();
            return;
        }

        await imageUpload(file);
    };
    return <>
        <Header
            loader={false}
            title=""
            type={uiType}
            userActive={true}
            restoName={resto?.name || ""}
            restoAddress={resto?.address || ""}
            name={infoHost?.name || ""}
            avatar={avatar}
            bannerImage={headerImage}
            welcome={showWelcome}
            showRestoAddress={showRestoAddress}
            resto={showResto}
            showName={showName}
            profile={showProfile}
            points={points?.points || 0}
            labelPoints="Puntos"
            loaderPoints={pointsLoader}
            showPoints={true}
            logoCountry={logoCountry}
            onImageUpload={handleHeaderImageUpload}
            onChangeAvatar={update}
            menuTabs={menuTabsWithLabels}
            onSelectMenuTab={(tab) => handleSelectTab(tab.id)}
        />

        {title && (
            <ViewTitle>{title}</ViewTitle>
        )}

        <BottomBar
            activeTab={activeTab}
            setActiveTab={handleSelectTab}
            tabs={tabsMobile}
            navigationTabs={bottomBarTabs}
            autoHeight={true}
            headerSize={{ height: 70 }}
        />

        {isHost && <ActiveModal isOpen={hostActive} onClose={() => setActiveHost(false)} />}

        {isHost && <TermsModal isOpen={openModalTerms} onClose={closeModalTerms} onAccept={handleAccept} />}

    </>;
}

const ViewTitle = styled.h1`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    margin: 12px 0 6px;
    padding: 0 16px;
    color: #111;
    font-weight: 600;
    font-size: 20px;
    text-align: center;
`;

const DesktopContainer = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
`;

export default MainPage;
